<script setup lang="ts">
import { computed, ref, watch, onMounted } from 'vue';
import { RevolverMatrixProps, TableInfo, defaults } from './RevolverMatrix.ts';
import { TableGridItem } from '@/models/table-grid-interface';
import { fetchDownline } from '@/utils/fetch-downline-service';
import { useAccountId, useToast } from '@/composables';
import { apiErrorToString, useAccountPositionApi } from '@ui-api3-sdk/api/api3';
import { useCacheStore } from '@/pinia/useCacheStore';

import MemberInfoPopup from './base/MemberInfoPopup.vue';
import RevolverMatrixTabs from './RevolverMatrix/RevolverMatrixTabs.vue';
import RevolverMatrixLevel from './RevolverMatrix/RevolverMatrixLevel.vue';
import EditProperty from './base/EditProperty.vue';
import { Item } from '@/models/table-grid-interface';
import { globalSpilloverDirection } from './PropertiesBox.ts';

const props = withDefaults(defineProps<RevolverMatrixProps>(), {
  id: undefined,
  headerTitles: undefined,
  inputProperties: undefined,
  popupProperties: undefined,
  properties: undefined,
  tables: () => defaults.tables,
  noTableFoundText: () => defaults.noTableFoundText,

  width: () => defaults.width,
  treeId: () => defaults.treeId,
  titleField: () => defaults.titleField,
  subtitleField: () => defaults.subtitleField,
  titleFieldMax: () => defaults.titleFieldMax,
  subtitleFieldMax: () => defaults.subtitleFieldMax,

  // Wrapper Styles
  backgroundColor: () => defaults.backgroundColor,
  tabsWrapperClass: () => defaults.tabsWrapperClass,
  tabsClass: () => defaults.tabsClass,
  matrixWrapperClass: () => defaults.matrixWrapperClass,
  contentWrapperClass: () => defaults.contentWrapperClass,
  loaderColor: () => defaults.loaderColor,
  tabsSliderColor: () => defaults.tabsSliderColor,
  tableChipColor: () => defaults.tableChipColor,

  // Card Styles
  cardBackgroundColor: () => defaults.cardBackgroundColor,
  emptySlotColor: () => defaults.emptySlotColor,
  cardClass: () => defaults.cardClass,
  emptyCardClass: () => defaults.emptyCardClass,
  cardTitleColor: () => defaults.cardTitleColor,
  cardSubtitleColor: () => defaults.cardSubtitleColor,
  arrowColor: () => defaults.arrowColor,

  // Avatar Sizes
  avatarSizeLevel0: () => defaults.avatarSizeLevel0,
  avatarSizeLevel1: () => defaults.avatarSizeLevel1,
  avatarSizeLevel2: () => defaults.avatarSizeLevel2,

  // Empty Slot Behavior
  forceMiniCards: () => defaults.forceMiniCards,
  showEmptyIcon: () => defaults.showEmptyIcon,
  emptyText: () => defaults.emptyText,

  // Editable Properties (for spillover)
  editableProperties: () => defaults.editableProperties,
  editPermission: () => defaults.editPermission,
});

const { aId: myAccountId } = useAccountId();

// State
const selectedTableIndex = ref(0);
const loading = ref(false);
const loadingPositions = ref(false);
const matrixData = ref<TableGridItem[]>([]);
const availableTables = ref<TableInfo[]>([]);

// Popup state
const infoPopupOpen = ref(false);
const infoPopupItem = ref<TableGridItem | undefined>();

// Computed
const selectedTable = computed<TableInfo | undefined>(() => {
  if (!availableTables.value || availableTables.value.length === 0) {
    return undefined;
  }
  const table = availableTables.value[selectedTableIndex.value];
  return table;
});

const hasNoTables = computed(() => {
  const result = !loadingPositions.value && availableTables.value.length === 0;
  return result;
});

// Matrix structure computed
const rootUsers = computed<(TableGridItem | null)[]>(() => {
  const user = matrixData.value.find(item => {
    const level = Number(item['vmdl.level']?.raw ?? item['t.level']?.raw ?? -1);
    return level === 0;
  });
  return [user || null];
});

const level1Users = computed<(TableGridItem | null)[]>(() => {
  const result: (TableGridItem | null)[] = [];
  for (let i = 0; i < props.width; i++) {
    const user = matrixData.value.find(item => {
      const level = Number(item['vmdl.level']?.raw ?? item['t.level']?.raw ?? -1);
      const number = Number(item['vmdl.number']?.raw ?? 0);
      return level === 1 && number === i + 1;
    });
    result.push(user || null);
  }
  return result;
});

const level2Users = computed<(TableGridItem | null)[]>(() => {
  const result: (TableGridItem | null)[] = [];
  const totalSlots = props.width * props.width;

  for (let i = 0; i < totalSlots; i++) {
    const parentIndex = Math.floor(i / props.width);
    const parentUser = level1Users.value[parentIndex];

    if (parentUser) {
      const parentPositionId = Number(parentUser['vmdl.id']?.raw ?? 0);
      const slotNumber = (i % props.width) + 1;

      const user = matrixData.value.find(item => {
        const level = Number(item['vmdl.level']?.raw ?? item['t.level']?.raw ?? -1);
        const parentPosId = Number(item['vmdl.parentPositionId']?.raw ?? 0);
        const number = Number(item['vmdl.number']?.raw ?? 0);
        return level === 2 && parentPosId === parentPositionId && number === slotNumber;
      });
      result.push(user || null);
    } else {
      result.push(null);
    }
  }
  return result;
});

// Methods
function selectTable(index: number) {
  selectedTableIndex.value = index;
}

// Fetch all user positions and filter by table mapping
async function fetchPositions() {
  if (!myAccountId.value) {
    return;
  }

  if (!props.tables || props.tables.length === 0) {
    console.warn('[RevolverMatrix] fetchPositions: No tables mapping provided!');
    availableTables.value = [];
    return;
  }

  const accountId = myAccountId.value;
  loadingPositions.value = true;
  try {
    const positionResult = await useAccountPositionApi().getPosition({
      id: accountId,
      treeId: props.treeId,
      offset: -1,
      properties: [],
    });

    if (!positionResult?.data?.payload) {
      availableTables.value = [];
      return;
    }

    const positions = positionResult.data.payload;

    // Build tables array based on the order in props.tables (array format)
    const tables: TableInfo[] = [];
    props.tables.forEach(tableEntry => {
      const position = positions.find(p =>
        p.treeId === tableEntry.treeId && p.offset === tableEntry.offset
      );

      if (position) {
        tables.push({
          id: position.id,
          name: tableEntry.name,
          treeId: tableEntry.treeId,
          treeLevel: position.treeLevel,
          offset: tableEntry.offset,
        });
      }
    });

    availableTables.value = tables;
  } catch (e: any) {
    useToast().error(apiErrorToString(e));
    availableTables.value = [];
  } finally {
    loadingPositions.value = false;
  }
}

async function fetchMatrixData() {
  if (!selectedTable.value) {
    return;
  }
  if (!myAccountId.value) {
    return;
  }

  loading.value = true;
  try {

    const result = await fetchDownline({
      treeId: selectedTable.value.treeId,
      accountId: myAccountId.value,
      positionId: Number(selectedTable.value.id), // Use position ID from selected table
      properties: props.properties || [],
      depth: 3,
      pagination: {
        page: 1,
        limit: 100,
      },
    });

    matrixData.value = result.tableItems;

    // Save root item to cache for ui-properties-box to work
    const rootUser = matrixData.value.find(item => {
      const level = Number(item['vmdl.level']?.raw ?? item['t.level']?.raw ?? -1);
      return level === 0;
    });
    if (rootUser) {
      useCacheStore().updateCache(rootUser, myAccountId.value);
    }
  } catch (e: any) {
    useToast().error(apiErrorToString(e));
    matrixData.value = [];
  } finally {
    loading.value = false;
  }
}

function openInfoPopup(item: TableGridItem | null) {
  if (!item) return;
  infoPopupItem.value = item;
  infoPopupOpen.value = true;
}

// Lifecycle hooks
onMounted(() => {
  // Always fetch positions
  fetchPositions();
});

// Watch for accountId changes to refetch positions
watch(myAccountId, () => {
  fetchPositions();
});

// Watch for available tables to load first table's data automatically
watch(availableTables, (newTables) => {
  if (newTables.length > 0 && selectedTableIndex.value === 0) {
    // Automatically load first table
    fetchMatrixData();
  }
});

// Watch for table changes - only fetch matrix data when we have a selected table
watch([selectedTable, myAccountId], () => {
  if (selectedTable.value) {
    fetchMatrixData();
  }
});

// Computed for root item (used in above slot)
const rootItem = computed(() => {
  const user = rootUsers.value[0];
  return user ? Item(user) : undefined;
});

// Check if current user can edit properties
function canEditPropertiesOf(accountId: number | undefined) {
  const result = (() => {
    if (!accountId) return false;
    if (!myAccountId?.value) return false;
    if (props.editPermission === 'all') return true;
    if (props.editPermission === 'self') return accountId === myAccountId.value;
    return false;
  })();

  return result;
}
</script>

<template>
  <v-card :key="globalSpilloverDirection || 0" :id="id" elevation="0" class="rounded-lg" :class="matrixWrapperClass">

    <slot
      name="above"
      :item="rootItem"
      :loading="loading"
      :data-loading="loading ? 'true' : undefined"
      :canEdit="canEditPropertiesOf(rootItem?.accountId())"
      :myId="myAccountId"
      :treeId="props.treeId"
      :offset="selectedTable?.offset"
    />

    <!-- Area 1: Table tabs -->
    <RevolverMatrixTabs
      v-if="availableTables && availableTables.length > 0"
      :tables="availableTables"
      :selectedIndex="selectedTableIndex"
      :backgroundColor="backgroundColor"
      :tabsWrapperClass="tabsWrapperClass"
      :tabsClass="tabsClass"
      :tabsSliderColor="tabsSliderColor"
      :tableChipColor="tableChipColor"
      @select="selectTable"
    />

    <!-- Area 2: Matrix content -->
    <v-card-text
      :style="{ backgroundColor }"
      :class="contentWrapperClass"
    >
    <!-- Loading positions state -->
    <v-expand-transition>
      <v-progress-linear
        v-if="loadingPositions"
        height="6"
        :rounded="true"
        indeterminate
        :color="loaderColor"
      />
    </v-expand-transition>

    <!-- No tables found message -->
    <div v-if="hasNoTables" class="d-flex flex-column align-center justify-center pa-8">
      <v-icon size="64" color="grey-lighten-1" class="mb-4">
        mdi-table-off
      </v-icon>
      <div class="text-h6 text-grey-darken-1">
        {{ noTableFoundText }}
      </div>
    </div>

    <!-- Matrix content -->
    <v-expand-transition>
      <v-progress-linear
        v-if="loading && !hasNoTables && !loadingPositions"
        height="6"
        :rounded="true"
        indeterminate
        :color="loaderColor"
      />

      <div v-else-if="!hasNoTables && !loadingPositions" class="d-flex flex-column align-center">
        <!-- Level 0: Root user -->
        <RevolverMatrixLevel
          :users="rootUsers"
          :level="0"
          :width="width"
          :titleField="titleField"
          :subtitleField="subtitleField"
          :titleFieldMax="titleFieldMax"
          :subtitleFieldMax="subtitleFieldMax"
          :cardBackgroundColor="cardBackgroundColor"
          :backgroundColor="backgroundColor"
          :emptySlotColor="emptySlotColor"
          :cardClass="cardClass"
          :emptyCardClass="emptyCardClass"
          :cardTitleColor="cardTitleColor"
          :cardSubtitleColor="cardSubtitleColor"
          :avatarSizeLevel0="avatarSizeLevel0"
          :avatarSizeLevel1="avatarSizeLevel1"
          :avatarSizeLevel2="avatarSizeLevel2"
          :forceMiniCards="forceMiniCards"
          :showEmptyIcon="showEmptyIcon"
          :emptyText="emptyText"
          :arrowColor="arrowColor"
          @cardClick="openInfoPopup"
        />

        <!-- Level 1: width users -->
        <RevolverMatrixLevel
          :users="level1Users"
          :level="1"
          :width="width"
          :titleField="titleField"
          :subtitleField="subtitleField"
          :titleFieldMax="titleFieldMax"
          :subtitleFieldMax="subtitleFieldMax"
          :cardBackgroundColor="cardBackgroundColor"
          :backgroundColor="backgroundColor"
          :emptySlotColor="emptySlotColor"
          :cardClass="cardClass"
          :emptyCardClass="emptyCardClass"
          :cardTitleColor="cardTitleColor"
          :cardSubtitleColor="cardSubtitleColor"
          :avatarSizeLevel0="avatarSizeLevel0"
          :avatarSizeLevel1="avatarSizeLevel1"
          :avatarSizeLevel2="avatarSizeLevel2"
          :forceMiniCards="forceMiniCards"
          :showEmptyIcon="showEmptyIcon"
          :emptyText="emptyText"
          :arrowColor="arrowColor"
          @cardClick="openInfoPopup"
        />

        <!-- Level 2: width^2 users -->
        <RevolverMatrixLevel
          :users="level2Users"
          :level="2"
          :width="width"
          :titleField="titleField"
          :subtitleField="subtitleField"
          :titleFieldMax="titleFieldMax"
          :subtitleFieldMax="subtitleFieldMax"
          :cardBackgroundColor="cardBackgroundColor"
          :emptySlotColor="emptySlotColor"
          :backgroundColor="backgroundColor"
          :cardClass="cardClass"
          :emptyCardClass="emptyCardClass"
          :cardTitleColor="cardTitleColor"
          :cardSubtitleColor="cardSubtitleColor"
          :avatarSizeLevel0="avatarSizeLevel0"
          :avatarSizeLevel1="avatarSizeLevel1"
          :avatarSizeLevel2="avatarSizeLevel2"
          :forceMiniCards="forceMiniCards"
          :showEmptyIcon="showEmptyIcon"
          :emptyText="emptyText"
          :arrowColor="arrowColor"
          @cardClick="openInfoPopup"
        />
      </div>
    </v-expand-transition>

    </v-card-text>

    <!-- User info popup -->
    <MemberInfoPopup
      v-if="infoPopupItem"
      v-model="infoPopupOpen"
      :treeId="selectedTable?.treeId ?? props.treeId"
      :properties="popupProperties || []"
      :data="infoPopupItem"
      :propertyTitles="headerTitles"
      :buttonMode="false"
      :titleField="titleField"
      :subtitleField="subtitleField"
    />
  </v-card>
</template>
